import { useCallback } from 'react';

import { OasMemoryError } from 'core/atoms/errors';

import { DataError } from 'modules/planner/dna/types';
import { useMutation, useApolloClient } from 'core/dna/types/apollo';
import { CreateLessonGroupInput } from 'core/dna/types/generated/_globalTypes';
import { GetSchedulesSelectedState } from 'core/dna/types/generated/GetSchedulesSelectedState';

import { GET_SCHEDULES_SELECTED_STATE_QUERY } from 'modules/planner/memory/apollo/schedules/local';
import { GET_SCHEDULE_QUERY } from 'modules/planner/memory/apollo/schedules/remote';

import { CREATE_LESSON_GROUP_MUTATION } from './create-lesson-group-mutation';

export interface UseCreateLessonGroupResult {
  createLessonGroup: (input: CreateLessonGroupInput) => Promise<any>;
  loading: boolean;
  error?: DataError;
}

const TITLE = 'UseCreateLessonGroup';

export const useCreateLessonGroup = (): UseCreateLessonGroupResult => {
  const [createLessonGroup, { loading, error }] = useMutation(
    CREATE_LESSON_GROUP_MUTATION,
  );
  const client = useApolloClient();

  const func = useCallback(
    (input: CreateLessonGroupInput) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<any>(async (resolve, reject) => {
        try {
          const getScheduleStateSelectedResult = await client.query<GetSchedulesSelectedState>(
            {
              query: GET_SCHEDULES_SELECTED_STATE_QUERY,
            },
          );

          const scheduleIds =
            getScheduleStateSelectedResult.data?.schedulesState?.selected;

          if (scheduleIds?.length !== 1) {
            throw new OasMemoryError(
              'Schedule set incorrectly! Only one is expected.',
              { title: TITLE },
            );
          }

          const executionResult = await createLessonGroup({
            variables: {
              input: {
                ...input,
              },
            },
            refetchQueries: [
              {
                query: GET_SCHEDULE_QUERY,
                variables: { scheduleId: scheduleIds[0] },
              },
            ],
          });

          if (executionResult.data && !executionResult.errors) {
            resolve(executionResult);
          } else {
            reject(new Error('createLessonGroup error!'));
          }
        } catch (e: any) {
          reject(e);
        }
      });
    },
    [client, createLessonGroup],
  );

  return {
    createLessonGroup: func,
    loading,
    error,
  };
};
